import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CONVERSATION_CHAT_MODELS,
  DEFAULT_CONVERSATION_CHAT_MODEL,
  isConversationChatModel,
  type ConversationChatModelId,
} from "@mycompbox/shared";
import { ArrowRight, Loader2, MessagesSquare, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RunOnMachineDialog } from "./run-on-machine-dialog";
import { ImprovePlanDialog } from "@/components/epics/improve-plan-dialog";
import { useContinueInterview, useInterviewTranscript } from "@/hooks/queries/use-interviews";
import { useToast } from "@/hooks/useToast";
import { useJobStream } from "@/hooks/queries/use-mailbox";
import { DraftReplyBubble } from "@/components/mailbox/draft-reply-bubble";
import type { LinkedInterviewSummary } from "@/lib/api/epic-requests";
import type { InterviewTranscriptTurn } from "@/lib/api/interviews";

interface DiscussRequestSectionProps {
  request: {
    id: string;
    identifier?: string | null;
    title: string;
    status: string;
    convertedEpicId?: string | null;
    team?: { gitRemote?: string | null } | null;
  };
  /**
   * The request's linked interview (owner-scoped — undefined for anyone but the
   * creator, who then simply starts their own discussion).
   */
  linkedInterview?: LinkedInterviewSummary | null | undefined;
}

/**
 * Turn a discussion transcript into notes for the planner (ENG-291). The
 * caller (ImprovePlanDialog) truncates to the planner's notes limit.
 */
export function buildDiscussionNotes(
  request: { identifier?: string | null; title: string },
  turns: readonly Pick<InterviewTranscriptTurn, "role" | "content">[]
): string {
  const label = request.identifier ? `${request.identifier} (${request.title})` : request.title;
  const lines = [
    `Follow-up discussion of the Epic Request ${label}. Revise the plan to reflect it.`,
  ];
  for (const turn of turns) {
    const content = turn.content.trim();
    if (!content) continue;
    lines.push(`${turn.role === "user" ? "Person" : "Formulator"}: ${content}`);
  }
  return lines.join("\n\n");
}

/**
 * "Discuss this request" (ENG-291): available on every Epic Request, whatever
 * its status. Resumes the request's live discussion, continues a finished one,
 * or starts a new interview linked to the request — the conversation itself
 * runs in the InterviewFlowPanel on the job page. For a converted request, the
 * discussion can be sent to the planner as a refine run of the created epic.
 */
/** localStorage key for the person's chosen chat model (ENG-390). */
export const CHAT_MODEL_STORAGE_KEY = "compbox.chatModel";

/** The stored chat model, or Haiku 5.5 when it's missing or not in the list. */
export function readStoredChatModel(): ConversationChatModelId {
  try {
    const stored = window.localStorage.getItem(CHAT_MODEL_STORAGE_KEY);
    return isConversationChatModel(stored) ? stored : DEFAULT_CONVERSATION_CHAT_MODEL;
  } catch {
    return DEFAULT_CONVERSATION_CHAT_MODEL;
  }
}

function storeChatModel(model: ConversationChatModelId): void {
  try {
    window.localStorage.setItem(CHAT_MODEL_STORAGE_KEY, model);
  } catch {
    // Storage unavailable (private mode etc.) — the choice just won't persist.
  }
}

export function DiscussRequestSection({ request, linkedInterview }: DiscussRequestSectionProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const continueInterview = useContinueInterview();
  const [chatModel, setChatModel] = useState<ConversationChatModelId>(readStoredChatModel);
  const handleChatModelChange = (value: string) => {
    if (!isConversationChatModel(value)) return;
    setChatModel(value);
    storeChatModel(value);
  };

  const live =
    linkedInterview?.status === "in_progress" && linkedInterview.jobId ? linkedInterview : null;
  const finished = linkedInterview?.status === "completed" ? linkedInterview : null;

  // ENG-389: preview the formulator's reply while it streams in a live discussion.
  const { draft } = useJobStream(live?.jobId ?? undefined, { enabled: !!live });

  const convertedEpicId = request.status === "converted" ? (request.convertedEpicId ?? null) : null;
  const canSendToPlanner = !!convertedEpicId && !!linkedInterview;
  const { data: transcript } = useInterviewTranscript(linkedInterview?.interviewId, {
    enabled: canSendToPlanner,
  });
  const plannerNotes = transcript ? buildDiscussionNotes(request, transcript.turns) : undefined;

  const handleContinue = async () => {
    if (!finished) return;
    try {
      const result = await continueInterview.mutateAsync(finished.interviewId);
      void navigate(`/mailbox/jobs/${result.jobId}`);
    } catch (error) {
      toast(error instanceof Error ? error.message : "Failed to continue the discussion");
    }
  };

  return (
    <div className="border-t border-white/[0.06] px-4 md:px-6 py-4">
      <div className="flex items-center gap-2 mb-2">
        <MessagesSquare className="h-4 w-4 text-purple-300" />
        <span className="text-sm font-semibold text-purple-300">Discuss this request</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Talk this request through with the request formulator. The request is updated in place.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {live ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void navigate(`/mailbox/jobs/${live.jobId ?? ""}`);
            }}
          >
            <ArrowRight className="h-3.5 w-3.5 mr-2" />
            Discuss this request
          </Button>
        ) : finished ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void handleContinue();
            }}
            disabled={continueInterview.isPending}
          >
            {continueInterview.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
            ) : (
              <MessagesSquare className="h-3.5 w-3.5 mr-2" />
            )}
            Discuss this request
          </Button>
        ) : (
          <RunOnMachineDialog epicRequestId={request.id} chatModel={chatModel}>
            <MessagesSquare className="h-3.5 w-3.5 mr-2" />
            Discuss this request
          </RunOnMachineDialog>
        )}
        {canSendToPlanner && convertedEpicId && (
          <ImprovePlanDialog
            epicId={convertedEpicId}
            targetRemote={request.team?.gitRemote ?? null}
            initialNotes={plannerNotes}
            title="Send to planner"
          >
            <Send className="h-3.5 w-3.5 mr-2" />
            Send to planner
          </ImprovePlanDialog>
        )}
        {/* ENG-390: the picker only applies when a new conversation job is created. */}
        {!live && !finished && (
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            Chat model
            <select
              aria-label="Chat model"
              className="h-8 rounded-md border border-white/10 bg-transparent px-2 text-xs text-foreground"
              value={chatModel}
              onChange={(e) => {
                handleChatModelChange(e.target.value);
              }}
            >
              {CONVERSATION_CHAT_MODELS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {live && draft && (
        <div className="mt-3">
          <DraftReplyBubble text={draft} />
        </div>
      )}
    </div>
  );
}
